/**
 * WHAT A BOOKING RECORD IS FOR — the screen's copy of the API's
 * `booking_type` enum.
 *
 * Two of the three types are JOURNEYS. The third is not.
 *
 *   transfer   — a point-to-point or multi-stop trip.
 *   experience — a tour or guided activity.
 *   expense    — A COST THAT IS NOT A TRIP. Car rental, fuel, a parking
 *                bucket, an admin charge. Nobody is picked up and nobody is
 *                driven anywhere.
 *
 * Before the API grew the third value the office recorded those costs by
 * creating FAKE transfers — nine of them in production — because the form
 * had nowhere else to put a number. One of them has its whole expense
 * breakdown typed into the PICKUP ADDRESS field. That is the shape of the
 * problem this type exists to end.
 *
 * WHAT AN EXPENSE CARRIES (the API's required set):
 *   full_name       — THE DESCRIPTION. It is not a person. It is deliberately
 *                     this field and not a new one, because `full_name` is
 *                     already what every list, search and CSV renders.
 *   date_datetime   — required, AND MAY BE IN THE PAST. Expenses are
 *                     recorded after the money has gone.
 *   partner_id      — who bears the cost.
 *   currency        — the booking's currency.
 *   status          — as for any booking.
 *
 * The AMOUNT is `operators[].price` (with `operators[].currency`, else the
 * booking's). `price` / `original_price` are REVENUE and stay optional: an
 * expense may carry revenue when the cost is recharged.
 *
 * `send_notification_to_customer: true` is refused by the API. There is no
 * customer.
 */

/** Must match the API's enum. */
export const BOOKING_TYPES = ['transfer', 'experience', 'expense'];

/**
 * What a record with nothing stored is.
 *
 * `transfer` — matching the fallback every render site in this app already
 * applies (`BOOKING_TYPE_LABELS[t] || BOOKING_TYPE_LABELS.transfer`), so
 * nothing is reclassified by reading a booking through here.
 */
export const DEFAULT_BOOKING_TYPE = 'transfer';

/** A booking's type, however little the record carries. */
export const bookingTypeOf = (order) => (
  BOOKING_TYPES.includes(order?.booking_type)
    ? order.booking_type
    : DEFAULT_BOOKING_TYPE
);

/** A cost record rather than a journey. */
export const isExpenseBooking = (order) => bookingTypeOf(order) === 'expense';

/**
 * Does somebody actually travel on this booking?
 *
 * The complement of `isExpenseBooking`, named for the question the journey
 * features are really asking, so a call site cannot read as the wrong one.
 * Use this — not `booking_type !== 'experience'` — for anything that only
 * makes sense when a vehicle moves.
 */
export const isTripBooking = (order) => !isExpenseBooking(order);

/**
 * THE PER-ORDER STEPS AN EXPENSE DOES NOT HAVE.
 *
 * Every one of these describes something happening to a passenger or a
 * vehicle, so on a cost record they are meaningless noise and a standing
 * invitation to dispatch a driver to a fuel receipt. The API refuses the
 * same set; hiding them here is defence in depth and the reason the office
 * never sees the refusal.
 *
 * Deliberately a list of names rather than scattered `!== 'expense'` tests:
 * it is the one place to read off what an expense can and cannot do, and the
 * one place to change it when the API's set changes.
 */
export const EXPENSE_HIDDEN_STEPS = [
  'assign-driver',
  // Named to match the API's refusal set, though this UI has no separate
  // gate for it: one dropdown item produces a Trip Confirmation on a
  // transfer and a Voucher on an experience, and `generate-voucher` is the
  // name that item is gated by. Listed so the two halves can be read
  // against each other without hunting for a gate that was never missing.
  'trip-confirmation',
  'pickup-guide',
  'add-invoice',
  'generate-voucher',
  'driver-events',
];

/**
 * THE DETAILS TABS AN EXPENSE DOES NOT HAVE — the ones that describe a
 * journey. `extras` joins them because a booking extra is an opt-in service
 * sold to a passenger, which a cost record has none of.
 *
 * `overview`, `operators`, `financial`, `other` and `version-history` stay:
 * who we are paying and how much IS the record.
 */
export const EXPENSE_HIDDEN_TABS = [
  'route',
  'driver-events',
  'partner',
  'reminders',
  'extras',
];

/**
 * May this per-order step be offered on this booking?
 *
 * Phrased as an allow-question so a call site reads as the permission it is
 * asking for. Anything not named in `EXPENSE_HIDDEN_STEPS` is available on
 * every type, which keeps a step nobody has thought about visible rather
 * than silently gone.
 */
export const isStepAvailable = (order, step) => (
  !isExpenseBooking(order) || !EXPENSE_HIDDEN_STEPS.includes(step)
);

/** The same question for a tab in the Order Details modal. */
export const isTabAvailable = (order, tab) => (
  !isExpenseBooking(order) || !EXPENSE_HIDDEN_TABS.includes(tab)
);

/**
 * THE DEFAULT CURRENCY FOR A COST.
 *
 * Nearly every expense the office records — rental, fuel, parking, the
 * admin charges — is paid in forints, and the booking form otherwise opens
 * with no currency at all, which is one of the three things the API refuses
 * a new expense for. A transfer keeps its empty default: its currency is a
 * commercial fact about the sale and guessing it would be wrong.
 */
export const DEFAULT_EXPENSE_CURRENCY = 'HUF';

/**
 * WHAT THIS EXPENSE IS STILL MISSING, in the API's own words — or null when
 * it is complete.
 *
 * The cost operator and the amount ARE the record, and the API refuses a
 * save without them (`buildBookingsValidationSchema`, the expense-only
 * superRefine). This is the screen's copy of those two rules, deliberately
 * word-for-word: the office should read one sentence whether it is stopped
 * before the request or by the response to it.
 *
 * Both legacy shapes count, exactly as the API counts them: the `operators[]`
 * array the form sends, and the `operator_id` + `operator_price` lead that
 * most production bookings still carry.
 *
 * Returns null for anything that is not an expense — a trip has neither
 * requirement, and gating one on an operator would stop the office taking a
 * booking before it has found somebody to run it.
 */
export const expenseCostGap = (order) => {
  if (!isExpenseBooking(order)) return null;

  const entries = Array.isArray(order?.operators) ? order.operators : [];
  const named = entries.some((entry) => entry?.operator_id) || Boolean(order?.operator_id);

  if (!named) {
    return 'An expense must name the operator its cost is settled against';
  }

  const amount = entries.length
    ? entries.reduce((sum, entry) => sum + (Number(entry?.price) || 0), 0)
    : Number(order?.operator_price) || 0;

  if (amount <= 0) {
    return 'An expense must carry its cost amount on the operator';
  }

  return null;
};

export default {
  BOOKING_TYPES,
  DEFAULT_BOOKING_TYPE,
  bookingTypeOf,
  isExpenseBooking,
  isTripBooking,
  EXPENSE_HIDDEN_STEPS,
  EXPENSE_HIDDEN_TABS,
  isStepAvailable,
  isTabAvailable,
  DEFAULT_EXPENSE_CURRENCY,
  expenseCostGap,
};
